import React, { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { Cat } from 'lucide-react';
import { api, useServer } from '../contexts/ServerContext';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { steamIdTo64, getCachedAvatar, getCachedDisplayName } from '../utils/helpers';

const excludedCommands = [
  '/fb', '/f', '/me', '/try', '/do', '/looc', '/ooc', '/spectate', '!spectate'
];

function Punishments() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [pageInput, setPageInput] = useState(1);
  const { server } = useServer();
  const [detailsOpen, setDetailsOpen] = useState({});
  const [avatars, setAvatars] = useState({});
  const [typeFilter, setTypeFilter] = useState('');
  const [excludeMode, setExcludeMode] = useState(false);
  const [excludedTypes, setExcludedTypes] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const debounceRef = useRef();
  const [names, setNames] = useState({});

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, excludeMode, excludedTypes, search, server]);

  useEffect(() => {
    setPageInput(currentPage);
  }, [currentPage]);

  useEffect(() => {
    // Получаем steamId64 для всех логов на странице и грузим аватарки с кешем
    const steamIds = logs.map(log => log.steamID).filter(Boolean);
    const uniqueIds = Array.from(new Set(steamIds));
    uniqueIds.forEach(async (steamId) => {
      if (!avatars[steamId]) {
        try {
          const id64 = steamIdTo64(steamId);
          if (!id64) return;
          const url = await getCachedAvatar(id64, async () => {
            const resp = await api.get(`/api/steam/avatar/${id64}`);
            return resp.data.avatar;
          });
          setAvatars(prev => ({ ...prev, [steamId]: url }));
        } catch {
          setAvatars(prev => ({ ...prev, [steamId]: null }));
        }
      }
      if (!names[steamId]) {
        try {
          const id64 = steamIdTo64(steamId);
          if (!id64) return;
          const displayName = await getCachedDisplayName(id64, async () => {
            const resp = await api.get(`/api/players/${steamId}`);
            return resp.data.displayName || resp.data.name || steamId;
          });
          setNames(prev => ({ ...prev, [steamId]: displayName }));
        } catch {
          setNames(prev => ({ ...prev, [steamId]: steamId }));
        }
      }
    });
    // Аватарки для цели
    const targetSteamIds = logs.map(log => log.targetSteamID).filter(Boolean);
    const uniqueTargetIds = Array.from(new Set(targetSteamIds));
    uniqueTargetIds.forEach(async (steamId) => {
      if (!avatars[steamId]) {
        try {
          const id64 = steamIdTo64(steamId);
          if (!id64) return;
          const url = await getCachedAvatar(id64, async () => {
            const resp = await api.get(`/api/steam/avatar/${id64}`);
            return resp.data.avatar;
          });
          setAvatars(prev => ({ ...prev, [steamId]: url }));
        } catch {
          setAvatars(prev => ({ ...prev, [steamId]: null }));
        }
      }
      if (!names[steamId]) {
        try {
          const id64 = steamIdTo64(steamId);
          if (!id64) return;
          const displayName = await getCachedDisplayName(id64, async () => {
            const resp = await api.get(`/api/players/${steamId}`);
            return resp.data.displayName || resp.data.name || steamId;
          });
          setNames(prev => ({ ...prev, [steamId]: displayName }));
        } catch {
          setNames(prev => ({ ...prev, [steamId]: steamId }));
        }
      }
    });
  }, [logs]);

  // Получаем все типы с сервера (чтобы фильтр был по всем, а не только по текущей странице)
  useEffect(() => {
    async function fetchTypes() {
      try {
        const res = await api.get('/api/punishments/types');
        setAllTypes(res.data.types || []);
      } catch {
        setAllTypes([]);
      }
    }
    fetchTypes();
  }, [server]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `/api/punishments?page=${currentPage}&limit=${pageSize}&search=${encodeURIComponent(search)}`;
      if (!excludeMode && typeFilter === 'Command') {
        url += '&virtualType=Command';
      } else if (!excludeMode && typeFilter === 'Chat') {
        url += '&virtualType=Chat';
      } else if (!excludeMode && typeFilter && typeFilter !== 'WhiteList' && typeFilter !== 'Rank' && typeFilter !== 'Fraction' && typeFilter !== 'Faction') {
        url += `&type=${encodeURIComponent(typeFilter)}`;
      }
      if (excludeMode && excludedTypes.length > 0) {
        url += `&excludeTypes=${excludedTypes.join(',')}`;
      }
      if (!excludeMode && typeFilter === 'WhiteList') {
        url += '&fractionType=WhiteList';
      } else if (!excludeMode && typeFilter === 'Rank') {
        url += '&fractionType=Rank';
      } else if (!excludeMode && (typeFilter === 'Faction' || typeFilter === 'Fraction')) {
        url += '&fractionType=Fraction';
      }
      const response = await api.get(url);
      setLogs(response.data.punishments || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      setLogs([]);
      setTotalPages(1);
      toast.error('Ошибка при загрузке логов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchLogs();
    }, 300);
  }, [excludeMode, excludedTypes, typeFilter, currentPage, search, server]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gmod-white">Логи</h1>
        <p className="text-gmod-white opacity-60 mt-2">Список всех действий на сервере</p>
      </div>
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          placeholder="Поиск по SteamID, имени, типу, цели..."
          className="w-full px-4 py-2 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white placeholder-gmod-white placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-theme-primary"
        />
      </div>
      <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer p-6">
        <div className="mb-2 flex flex-wrap gap-2 items-center">
          <span className="text-gmod-white">Фильтр по типу:</span>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="bg-gmod-lighter text-gmod-white rounded px-2 py-1" disabled={excludeMode}>
            <option value="">Все</option>
            {allTypes.filter(type => type !== 'WhiteList' && type !== 'Rank' && type !== 'Fraction' && type !== 'Faction').map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
            <option value="WhiteList">WhiteList</option>
            <option value="Rank">Rank</option>
            <option value="Faction">Faction</option>
          </select>
          <label className="flex items-center ml-4 cursor-pointer">
            <input type="checkbox" checked={excludeMode} onChange={e => setExcludeMode(e.target.checked)} className="mr-1" />
            <span className="text-gmod-white">Исключить выбранные типы</span>
          </label>
          {excludeMode && (
            <div className="flex flex-wrap gap-2 ml-4">
              {allTypes.map(type => (
                <label key={type} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludedTypes.includes(type)}
                    onChange={e => {
                      if (e.target.checked) setExcludedTypes(prev => [...prev, type]);
                      else setExcludedTypes(prev => prev.filter(t => t !== type));
                    }}
                    className="mr-1"
                  />
                  <span className="text-gmod-white text-xs">{type}</span>
                </label>
              ))}
            </div>
          )}
          <button
            className="ml-4 px-3 py-1 bg-gmod-lighter text-gmod-white rounded hover:bg-theme-primary border border-gmod-lighterer"
            onClick={() => {
              setTypeFilter('');
              setExcludeMode(false);
              setExcludedTypes([]);
              setSearch('');
            }}
          >
            Сбросить фильтры
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
          </div>
        ) : logs.length === 0 ? (
          <p className="text-gmod-white opacity-60 text-center py-8">Логи не найдены</p>
        ) : (
          <>
            <table className="min-w-full text-sm text-gmod-white opacity-80">
              <thead className="sticky top-0 bg-gmod-dark z-10">
                <tr>
                  <th className="px-4 py-2 text-left">Кто</th>
                  <th className="px-4 py-2 text-center">Тип</th>
                  <th className="px-4 py-2 text-center">Что</th>
                  <th className="px-4 py-2 text-left">Цель</th>
                  <th className="px-4 py-2 text-center">Значение</th>
                  <th className="px-4 py-2 text-center">До</th>
                  <th className="px-4 py-2 text-center">Фракция</th>
                  <th className="px-4 py-2 text-center">Дата</th>
                  {(user.rank === 'superadmin' || user.rank === 'yufuadmin') && (
                    <th className="px-4 py-2 text-center">Подробнее</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const date = log.date ? new Date(log.date * 1000) : null;
                  let type = log._type || '-';
                  let target = log.targetName || '-';
                  let value = log.value !== undefined ? String(log.value) : '-';
                  let oldValue = log.oldValue !== undefined ? String(log.oldValue) : '-';
                  let what = '-';
                  // Гарантируем, что !asay всегда Chat
                  if (typeof log.value === 'string' && /^!asay(\s|$)/.test(log.value)) {
                    type = 'Chat';
                  }
                  // Парсинг команд
                  if (
                    typeof log.value === 'string' &&
                    (/^[!/]/.test(log.value)) &&
                    !log.value.startsWith('//') && !log.value.startsWith("asay") &&
                    !excludedCommands.some(cmd => new RegExp(`^${cmd}(\\s|$)`).test(log.value)) &&
                    !/^(!asay|\/me|\/try|\/do)(\s|$)/.test(log.value)
                  ) {
                    type = 'Command';
                    const args = log.value.split(' ');
                   if (/^\/roll(\s|$)/.test(log.value) || /^\/give(\s|$)/.test(log.value)) {
                     if (args.length === 2) {
                       value = args[1];
                       oldValue = '-';
                       target = '-';
                     } else if (args.length === 3) {
                       value = args[1] + ' ' + args[2];
                       oldValue = '-';
                       target = '-';
                     } else {
                       value = args[0].replace(/^\//, '');
                       oldValue = '-';
                       target = '-';
                     }
                   } else if (args.length === 1) {
                     value = args[0].replace(/^(!|\/)/, '');
                     oldValue = '-';
                     target = '-';
                   } else if (args.length > 1) {
                     if (!args[1] || args[1] === '^') target = 'На себя';
                     else if (args[1] === '@') target = 'Перед собой';
                     else target = args[1];
                     what = args[0] ? args[0].slice(1) : '-';
                     if (args.length === 3) {
                       value = args[2];
                       oldValue = '-';
                     } else if (args.length >= 4) {
                       oldValue = args[2];
                       value = args[3];
                     } else {
                       value = '-';
                       oldValue = '-';
                     }
                   }
                  } else if (type === '-' && typeof log.value === 'string') {
                    type = 'Chat';
                  }
                  // Цвета бейджей по типу
                  const typeColors = {
                    Ban: 'bg-red-600 text-white',
                    Kick: 'bg-yellow-500 text-white',
                    Mute: 'bg-blue-500 text-white',
                    Gag: 'bg-blue-400 text-white',
                    Slay: 'bg-orange-500 text-white',
                    Kill: 'bg-red-700 text-white',
                    Command: 'bg-theme-primary text-white',
                    Chat: 'bg-gmod-lighterer text-gmod-white',
                    WhiteList: 'bg-green-600 text-white',
                    Rank: 'bg-purple-600 text-white',
                    Fraction: 'bg-pink-600 text-white',
                  };
                  return (
                    <tr key={log.id + log.table} className="border-b border-gmod-lighterer hover:bg-gmod-lighter/40 transition">
                      <td className="px-4 py-2 text-left">
                        <span className="flex items-center gap-2">
                          {avatars[log.steamID] ? (
                            <img src={avatars[log.steamID]} alt="avatar" className="w-8 h-8 min-w-[32px] rounded-full border-2 border-theme-primary" />
                          ) : (
                            <Cat className="w-8 h-8 min-w-[32px] text-gmod-white opacity-40" />
                          )}
                          <div className="flex flex-col">
                            <Link to={`/players/${log.steamID}`} className="text-theme-primary font-semibold hover:underline truncate max-w-[120px]">{names[log.steamID] || log.name}</Link>
                            <span className="block text-xs text-gmod-white opacity-60 font-mono cursor-pointer hover:text-theme-primary" onClick={() => {setSearch(log.steamID); setCurrentPage(1);}}>{log.steamID || '-'}</span>
                          </div>
                        </span>
                      </td>
                      <td className={`px-4 py-2 text-center cursor-pointer hover:text-theme-primary`} onClick={() => { setTypeFilter(type); setCurrentPage(1); }}>{
                        <span className={`inline-block px-2 py-1 rounded font-bold text-xs ${typeColors[type] || 'bg-gmod-lighterer text-gmod-white'}`}>{type}</span>
                      }</td>
                      <td className="px-4 py-2 text-center">{what}</td>
                      <td className="px-4 py-2 text-left">
                        <span className="flex items-center gap-2">
                          {log.targetSteamID && log.targetSteamID !== '-' ? (
                            <>
                              {avatars[log.targetSteamID] ? (
                                <img src={avatars[log.targetSteamID]} alt="avatar" className="w-8 h-8 min-w-[32px] rounded-full border-2 border-theme-primary" />
                              ) : (
                                <Cat className="w-8 h-8 min-w-[32px] text-gmod-white opacity-40" />
                              )}
                              <div className="flex flex-col">
                                <Link to={`/players/${log.targetSteamID}`} className="text-theme-primary font-semibold hover:underline truncate max-w-[120px]">{names[log.targetSteamID] || target}</Link>
                                <span className="block text-xs text-gmod-white opacity-60 font-mono cursor-pointer hover:text-theme-primary" onClick={() => {setSearch(log.targetSteamID); setCurrentPage(1);}}>{log.targetSteamID}</span>
                              </div>
                            </>
                          ) : target}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">{
                        (typeof value === 'string' && (/(.png|.jpg)$/i).test(value.trim())) ? (
                          <img src={value.trim()} alt="img" className="max-h-16 max-w-xs mx-auto rounded border border-gmod-lighterer" />
                        ) : value
                      }</td>
                      <td className="px-4 py-2 text-center">{oldValue}</td>
                      <td className="px-4 py-2 text-center">{log.faction || '-'}</td>
                      <td className="px-4 py-2 text-center">
                        <span className="inline-flex items-center gap-1">
                          <svg className="w-4 h-4 text-gmod-yellow" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                          {date ? date.toLocaleString('ru-RU') : '-'}
                        </span>
                      </td>
                      {(user.rank === 'superadmin' || user.rank === 'yufuadmin') && (
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => setDetailsOpen(prev => ({ ...prev, [log.id + log.table]: !prev[log.id + log.table] }))}
                            className="px-3 py-1 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover"
                          >
                            {detailsOpen[log.id + log.table] ? 'Скрыть' : 'Подробнее'}
                          </button>
                          {detailsOpen[log.id + log.table] && (
                            <div className="mt-2 bg-gmod-lighter rounded p-2 text-left max-w-lg mx-auto text-xs text-gmod-white">
                              <div className="mb-1 font-bold text-theme-primary">Все поля лога:</div>
                              <pre className="whitespace-pre-wrap break-all">{JSON.stringify(log, null, 2)}</pre>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex justify-center space-x-2 mt-4 items-center">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                >
                  Назад
                </button>
                <span className="px-2 text-gmod-white">Страница</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={pageInput}
                  onChange={e => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={() => {
                    let val = parseInt(pageInput);
                    if (isNaN(val) || val < 1) val = 1;
                    if (val > totalPages) val = totalPages;
                    setCurrentPage(val);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      let val = parseInt(pageInput);
                      if (isNaN(val) || val < 1) val = 1;
                      if (val > totalPages) val = totalPages;
                      setCurrentPage(val);
                    }
                  }}
                  className="w-14 px-2 py-1 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white text-center mx-1"
                  style={{width: 50}}
                />
                <span className="px-2 text-gmod-white">из {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gmod-lighterer"
                >
                  Вперёд
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Punishments; 